import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faTrashAlt } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  AveragePlayerScore,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { TotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';
import { cart } from '@sorare/core/src/lib/glossary';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';

import { AnyPlayerStatsIcon } from 'components/player/AnyPlayerStatsIcon';
import { FlexCard } from 'components/token/FlexCard';
import { useCart } from 'hooks/useCart';

import type {
  SavedForLaterItem_anyCard,
  SavedForLaterItem_cartItem,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 0.5 })`
  max-width: 112px;
  min-width: 92px;
  width: 100%;
`;
const Metas = styled(Vertical).attrs({ gap: 0.5 })`
  flex-grow: 1;
`;
const CardWrapper = styled.div`
  width: 100%;
`;

const Actions = styled(Vertical)`
  font: var(--t-label-m);
  color: var(--c-nd-600);
`;

type Props = {
  cartItem: Nullable<SavedForLaterItem_cartItem>;
  card: Nullable<SavedForLaterItem_anyCard>;
  onStatsClick: () => void;
};

export const SavedForLaterItem = ({ cartItem, card, onStatsClick }: Props) => {
  const {
    flags: { useMlbSeasonAverageScore = false },
  } = useFeatureFlags();
  const { addCartItemsToCart, removeFromCart } = useCart();
  const sport = card?.sport || Sport.FOOTBALL;

  const modePerSport: Record<Sport, TotalAveragePlayerScore> = {
    [Sport.FOOTBALL]: AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE,
    [Sport.NBA]: AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
    [Sport.BASEBALL]: useMlbSeasonAverageScore
      ? AveragePlayerScore.SEASON_AVERAGE_SCORE
      : AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE,
  };
  const mode = modePerSport[sport];
  if (!cartItem || !card) return null;

  return (
    <Root>
      <CardWrapper>
        <FlexCard card={card} width={80} />
      </CardWrapper>
      <Metas>
        <Horizontal>
          <AnyPlayerStatsIcon player={card?.anyPlayer} onClick={onStatsClick} />
          <PlayerScore small player={card.anyPlayer} mode={mode} />
        </Horizontal>
        {!!cartItem.price && (
          <AmountWithConversion column monetaryAmount={cartItem.price} />
        )}
        <Actions>
          <LoadingButton
            loading={addCartItemsToCart.loading}
            color="quaternary"
            size="small"
            onClick={async () =>
              addCartItemsToCart.mutate([cartItem.id], card.sport)
            }
          >
            <FormattedMessage {...cart.addToCart} />
          </LoadingButton>
          <LoadingButton
            loading={removeFromCart.loading}
            color="tertiary"
            size="small"
            onClick={() => {
              removeFromCart.mutate([cartItem.id]);
            }}
          >
            <FontAwesomeIcon icon={faTrashAlt} />
          </LoadingButton>
        </Actions>
      </Metas>
    </Root>
  );
};

SavedForLaterItem.fragments = {
  anyCard: gql`
    fragment SavedForLaterItem_anyCard on AnyCardInterface {
      slug
      serialNumber
      supply
      anyPlayer {
        slug
        displayName
        ...AnyPlayerStatsIcon_anyPlayer
        ...PlayerScore_anyPlayer
      }
      anyPositions
      ...FlexCard_anyCard
    }
    ${FlexCard.fragments.anyCard}
    ${AnyPlayerStatsIcon.fragments.anyPlayer}
    ${PlayerScore.fragments.anyPlayer}
  ` as TypedDocumentNode<SavedForLaterItem_anyCard>,
  cartItem: gql`
    fragment SavedForLaterItem_cartItem on CartItem {
      id
      state
      savedForLater
      price {
        ...MonetaryAmountFragment_monetaryAmount
      }
    }
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<SavedForLaterItem_cartItem>,
};
